import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ImagePlus, X, Pencil, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ImageEditor } from "@/components/organisms/image-editor";
import { pdfToImage } from "@/core/utils/pdf-to-image";

interface ZoneFormData {
  name: string;
  planBlob?: Blob;
  planType?: string;
}

export function ZoneForm({
  defaultValues,
  existingPlanBlob,
  onSubmit,
  redirectTo,
  planLocked,
}: {
  defaultValues?: { name: string };
  existingPlanBlob?: ArrayBuffer | Blob;
  onSubmit: (data: ZoneFormData) => Promise<void>;
  /** URL de redirection après submit. Si absent, navigate(-1) */
  redirectTo?: string;
  /** Si true, le plan ne peut pas être modifié (des pins existent) */
  planLocked?: boolean;
}) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(defaultValues?.name ?? "");
  const [saving, setSaving] = useState(false);
  // Convertir ArrayBuffer → Blob si nécessaire (Dexie stocke en ArrayBuffer pour Safari iOS)
  const [planFile, setPlanFile] = useState<Blob | null>(
    existingPlanBlob
      ? existingPlanBlob instanceof Blob
        ? existingPlanBlob
        : new Blob([existingPlanBlob], { type: "image/jpeg" })
      : null
  );
  const [planPreview, setPlanPreview] = useState<string | null>(null);
  const [removedPlan, setRemovedPlan] = useState(false);

  // Image brute en cours d'édition (avant crop/ajustements)
  const [rawImage, setRawImage] = useState<Blob | null>(null);
  const [editing, setEditing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Créer/libérer l'objectURL pour la preview
  useEffect(() => {
    if (planFile) {
      const url = URL.createObjectURL(planFile);
      setPlanPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPlanPreview(null);
  }, [planFile]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPdf = file.type === "application/pdf";
    const isImage = file.type.startsWith("image/");

    if (!isImage && !isPdf) {
      alert("Formats acceptés : JPG, PNG ou PDF.");
      return;
    }

    const MAX_SIZE = 20 * 1024 * 1024; // 20 Mo
    if (file.size > MAX_SIZE) {
      alert("Fichier trop volumineux (max 20 Mo).");
      return;
    }

    // Reset input pour permettre re-sélection du même fichier
    e.target.value = "";

    if (isPdf) {
      // Convertir le PDF en image avant d'ouvrir l'éditeur
      setConverting(true);
      setConvertError(null);
      try {
        const imageBlob = await pdfToImage(file);
        setRawImage(imageBlob);
        setEditing(true);
      } catch (err) {
        console.error("[PDF conversion]", err);
        setConvertError("Impossible de convertir ce PDF. Essayez avec une image (JPG, PNG).");
      } finally {
        setConverting(false);
      }
    } else {
      // Image classique : ouvrir l'éditeur directement
      setRawImage(file);
      setEditing(true);
    }
  }

  function handleEditorSave(editedBlob: Blob) {
    setPlanFile(editedBlob);
    setRemovedPlan(false);
    setEditing(false);
    setRawImage(null);
  }

  function handleEditorCancel() {
    setEditing(false);
    setRawImage(null);
  }

  function handleEditExisting() {
    if (planFile) {
      setRawImage(planFile);
      setEditing(true);
    }
  }

  function handleRemovePlan() {
    setPlanFile(null);
    setRemovedPlan(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    setSubmitError(null);
    try {
      const data: ZoneFormData = { name: name.trim() };

      if (planFile && !removedPlan) {
        data.planBlob = planFile;
        data.planType = "image";
      } else if (removedPlan) {
        data.planType = "default";
      }

      await onSubmit(data);
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate(-1);
      }
    } catch (err) {
      console.error("[ZoneForm] Erreur submit:", err);
      setSubmitError(err instanceof Error ? err.message : "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  }

  // Mode édition d'image : on remplace tout le formulaire par l'éditeur
  if (editing && rawImage) {
    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold">Ajuster l&apos;image du plan</h3>
        <ImageEditor
          imageBlob={rawImage}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="zone-name">Nom de la zone *</Label>
        <Input
          id="zone-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex : Sous-sol, Parking, Bâtiment A"
          required
          autoFocus
          maxLength={255}
        />
      </div>

      {/* Upload plan */}
      <div className="flex flex-col gap-2">
        <Label>Plan de la zone</Label>

        {converting ? (
          <div className="flex flex-col items-center gap-3 rounded-md border-2 border-dashed border-primary/30 py-8 text-muted-foreground">
            <Loader2 className="size-8 animate-spin text-primary" />
            <span className="text-sm font-medium">Conversion du PDF en cours...</span>
          </div>
        ) : convertError ? (
          <div className="flex flex-col items-center gap-3 rounded-md border-2 border-dashed border-destructive/30 py-8">
            <span className="text-sm text-destructive">{convertError}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setConvertError(null); fileInputRef.current?.click(); }}
            >
              Réessayer
            </Button>
          </div>
        ) : planPreview ? (
          <div className="relative overflow-hidden rounded-md border border-border">
            <img
              src={planPreview}
              alt="Aperçu du plan"
              className="max-h-48 w-full object-contain bg-muted"
            />
            {!planLocked && (
              <button
                type="button"
                onClick={handleRemovePlan}
                className="absolute top-2 right-2 rounded-full bg-background/80 p-1 backdrop-blur-sm"
              >
                <X className="size-4 text-destructive" />
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={planLocked}
            className="flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-border py-8 text-muted-foreground transition-colors hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ImagePlus className="size-8" />
            <span className="text-sm">Importer un plan</span>
            <span className="text-xs">JPG, PNG ou PDF</span>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,application/pdf"
          onChange={handleFileChange}
          className="hidden"
        />

        {planLocked && planPreview && (
          <p className="text-xs text-muted-foreground">
            Des points de mesure existent sur ce plan. Supprimez-les avant de modifier le plan.
          </p>
        )}

        {planPreview && !planLocked && (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleEditExisting}
            >
              <Pencil className="size-4" />
              Retoucher
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Changer
            </Button>
          </div>
        )}
      </div>

      {submitError && (
        <p className="text-sm text-destructive text-center">{submitError}</p>
      )}

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={() => navigate(-1)}
        >
          Annuler
        </Button>
        <Button type="submit" className="flex-1" disabled={!name.trim() || saving}>
          {saving ? "Enregistrement..." : defaultValues ? "Modifier" : "Créer"}
        </Button>
      </div>
    </form>
  );
}
